<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { formatCurrencyFromCents } from '../utils/currency';

const props = defineProps({
  serviceOrder: {
    type: Object,
    required: true,
  },
  pipeline: {
    type: String,
    default: 'service_orders',
  },
  currency: {
    type: String,
    default: 'USD',
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'click']);

const { locale, t } = useI18n();
const isActionMenuOpen = ref(false);

const translationScope = computed(() => {
  if (props.pipeline === 'support') {
    return 'CRM.PIPELINES.SUPPORT';
  }

  return 'CRM.PIPELINES.SERVICE_ORDERS';
});

const resolvedLocale = computed(() => {
  return (locale.value || 'en').replace('_', '-');
});

const formattedCreatedAt = computed(() => {
  if (!props.serviceOrder?.created_at) {
    return '—';
  }

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(props.serviceOrder.created_at));
});

const onEdit = () => {
  emit('edit', props.serviceOrder);
};

const onDelete = () => {
  emit('delete', props.serviceOrder);
};

const onCardClick = () => {
  emit('click', props.serviceOrder);
};

const toggleActionMenu = () => {
  isActionMenuOpen.value = !isActionMenuOpen.value;
};

const menuItems = computed(() => {
  const items = [
    {
      action: 'edit',
      value: 'edit',
      label: t(`${translationScope.value}.CARD.EDIT_ITEM`),
      icon: 'i-lucide-pencil-line',
    },
  ];

  if (props.canDelete) {
    items.push({
      action: 'delete',
      value: 'delete',
      label: t(`${translationScope.value}.CARD.DELETE_ITEM`),
      icon: 'i-lucide-trash-2',
    });
  }

  return items;
});

const contact = computed(() => props.serviceOrder?.contact || null);
const crmData = computed(() => props.serviceOrder?.crm_data || {});
const isSupportPipeline = computed(() => props.pipeline === 'support');
const formattedValue = computed(() => {
  return formatCurrencyFromCents({
    valueCents: crmData.value.value_cents,
    currency: props.currency,
    locale: locale.value,
  });
});
const dueDateLabel = computed(() => {
  const dueDate = crmData.value.due_date;
  if (!dueDate) {
    return null;
  }

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(dueDate));
});
const satisfactionLabel = computed(() => {
  if (!crmData.value.satisfaction_score) {
    return null;
  }

  return `${crmData.value.satisfaction_score}/5`;
});
const emotionLabel = computed(() => {
  if (!crmData.value.emotion_label) {
    return null;
  }

  return t(
    `CRM.MODAL.FORM.EMOTION_OPTIONS.${crmData.value.emotion_label.toUpperCase()}`
  );
});
const difficultyLabel = computed(() => {
  if (!crmData.value.difficulty_level) {
    return null;
  }

  return t('CRM.CARD.BADGES.DIFFICULTY_VALUE', {
    value: crmData.value.difficulty_level,
  });
});
const priorityLabel = computed(() => {
  if (!crmData.value.priority_level) {
    return null;
  }

  return t(
    `CRM.MODAL.FORM.PRIORITY_OPTIONS.${crmData.value.priority_level.toUpperCase()}`
  );
});

const contactName = computed(() => {
  if (contact.value?.name) {
    return contact.value.name;
  }

  if (props.serviceOrder?.contact_id) {
    return `#${props.serviceOrder.contact_id}`;
  }

  return null;
});

const contactThumbnail = computed(() => contact.value?.thumbnail || null);

const onAction = ({ action }) => {
  isActionMenuOpen.value = false;

  if (action === 'edit') {
    onEdit();
  }

  if (action === 'delete') {
    onDelete();
  }
};
</script>

<template>
  <div
    class="flex w-full min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 transition-all duration-200 ease-in-out"
    :class="
      isDragging
        ? 'shadow-xl ring-2 ring-n-blue-7 bg-n-solid-1 cursor-grabbing'
        : 'shadow-sm hover:border-n-slate-6 hover:bg-n-alpha-1 cursor-pointer'
    "
    role="button"
    @click="onCardClick"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex min-w-0 items-start gap-1.5">
        <span
          class="service-order-drag-handle mt-0.5 inline-flex cursor-grab text-n-slate-10 active:cursor-grabbing"
          :title="$t(`${translationScope}.CARD.DRAG_HANDLE`)"
          @click.stop
        >
          <span class="i-lucide-grip-vertical size-4" />
        </span>
        <h4
          class="min-w-0 pr-2 text-sm font-semibold text-n-slate-12 line-clamp-2"
        >
          {{ serviceOrder.title }}
        </h4>
      </div>

      <div
        v-on-clickaway="() => (isActionMenuOpen = false)"
        class="relative shrink-0"
        @click.stop
      >
        <NextButton
          variant="ghost"
          color="slate"
          size="xs"
          icon="i-lucide-ellipsis-vertical"
          :title="$t(`${translationScope}.CARD.MENU`)"
          @click.stop="toggleActionMenu"
        />

        <DropdownMenu
          v-if="isActionMenuOpen"
          :menu-items="menuItems"
          class="top-8 ltr:right-0 rtl:left-0"
          @action="onAction"
        />
      </div>
    </div>

    <p
      v-if="serviceOrder.description"
      class="text-xs text-n-slate-11 line-clamp-2"
    >
      {{ serviceOrder.description }}
    </p>

    <div class="flex min-w-0 flex-wrap items-center gap-1.5">
      <template v-if="isSupportPipeline">
        <span
          v-if="emotionLabel"
          class="inline-flex max-w-full items-center rounded-full bg-n-amber-3 px-2 py-0.5 text-[11px] font-medium text-n-amber-11 truncate"
        >
          {{ emotionLabel }}
        </span>
        <span
          v-if="satisfactionLabel"
          class="inline-flex max-w-full items-center rounded-full bg-n-emerald-3 px-2 py-0.5 text-[11px] font-medium text-n-emerald-11 truncate"
        >
          {{ $t('CRM.CARD.BADGES.SATISFACTION', { value: satisfactionLabel }) }}
        </span>
      </template>

      <template v-else>
        <span
          v-if="formattedValue"
          class="inline-flex max-w-full items-center rounded-full bg-n-blue-3 px-2 py-0.5 text-[11px] font-medium text-n-blue-11 truncate"
        >
          {{ formattedValue }}
        </span>
        <span
          v-if="difficultyLabel"
          class="inline-flex max-w-full items-center rounded-full bg-n-amber-3 px-2 py-0.5 text-[11px] font-medium text-n-amber-11 truncate"
        >
          {{ difficultyLabel }}
        </span>
        <span
          v-if="priorityLabel"
          class="inline-flex max-w-full items-center rounded-full bg-n-ruby-3 px-2 py-0.5 text-[11px] font-medium text-n-ruby-11 truncate"
        >
          {{ priorityLabel }}
        </span>
        <span
          v-if="dueDateLabel"
          class="inline-flex max-w-full items-center rounded-full bg-n-slate-3 px-2 py-0.5 text-[11px] font-medium text-n-slate-11 truncate"
        >
          {{ $t('CRM.CARD.BADGES.DUE_DATE', { date: dueDateLabel }) }}
        </span>
      </template>
    </div>

    <div class="flex min-w-0 items-center gap-3 border-t border-n-weak pt-2">
      <div v-if="contactName" class="flex min-w-0 flex-1 items-center gap-2">
        <Avatar
          :src="contactThumbnail"
          :size="18"
          :name="contactName"
          rounded-full
        />
        <span class="truncate text-xs text-n-slate-11 font-medium">
          {{ contactName }}
        </span>
      </div>

      <div v-else class="flex-1 text-xs text-n-slate-10 italic">
        {{ $t(`${translationScope}.CARD.NO_CONTACT`) }}
      </div>

      <span class="shrink-0 text-[11px] text-n-slate-10">
        {{ formattedCreatedAt }}
      </span>
    </div>
  </div>
</template>
